import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Layout } from "@/components/layout";
import { useProformaPayments, useConfirmProformaPayment, useInvestigateProformaPayment, usePayProformaInvoice, useClearAlreadyPostedProformaPayment, type ProformaPayment } from "@/hooks/use-proforma-payments";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  FileCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function formatMoney(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

function formatDate(dateString: string | null, includeTime: boolean = false) {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "—";
    
    const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
    if (includeTime) {
      opts.hour = "2-digit";
      opts.minute = "2-digit";
    }
    
    return new Intl.DateTimeFormat("en-GB", opts).format(d);
  } catch {
    return "—";
  }
}

export function isProformaActivelyProcessing(status: string): boolean {
  return status === "action_running" || status === "posting_running";
}

export default function CashReceiptsProformaPage() {
  const { user } = useAuth();
  const { data, isLoading, isFetching, error, refetch } = useProformaPayments();
  const confirmMutation = useConfirmProformaPayment();
  const investigateMutation = useInvestigateProformaPayment();
  const payInvoiceMutation = usePayProformaInvoice();
  const clearAlreadyPostedMutation = useClearAlreadyPostedProformaPayment();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [selectedPayment, setSelectedPayment] = useState<ProformaPayment | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoiceRef, setInvoiceRef] = useState("");

  const payments = useMemo(() => {
    if (!data?.payments) return [];
    
    const q = search.toLowerCase();
    return data.payments.filter((p) => {
      if (!q) return true;
      return (
        (p.narrative && p.narrative.toLowerCase().includes(q)) ||
        (p.account && p.account.toLowerCase().includes(q)) ||
        (p.salesOrder && p.salesOrder.toLowerCase().includes(q))
      );
    });
  }, [data, search]);

  const handleConfirmClick = (payment: ProformaPayment) => {
    setSelectedPayment(payment);
    setIsConfirmDialogOpen(true);
  };

  const executeConfirm = () => {
    if (!selectedPayment) return;
    
    confirmMutation.mutate(selectedPayment.id, {
      onSuccess: () => {
        setIsConfirmDialogOpen(false);
        setSelectedPayment(null);
        toast({
          title: "Confirmation completed",
          description: `Successfully processed confirmation for ${selectedPayment.salesOrder || "the payment"}.`,
        });
      },
      onError: (err) => {
        toast({
          variant: "destructive",
          title: "Confirmation failed",
          description: err.message || "An unknown error occurred.",
        });
      }
    });
  };

  const handlePayInvoiceClick = (payment: ProformaPayment) => {
    setSelectedPayment(payment);
    setInvoiceRef("");
    setIsInvoiceDialogOpen(true);
  };

  const executePayInvoice = () => {
    if (!selectedPayment || !invoiceRef.trim()) return;
    payInvoiceMutation.mutate({ id: selectedPayment.id, invoiceRef: invoiceRef.trim() }, {
      onSuccess: () => {
        setIsInvoiceDialogOpen(false);
        setSelectedPayment(null);
        setInvoiceRef("");
        toast({
          title: "Payment posted to invoice",
          description: `${formatMoney(selectedPayment.amount)} was posted to ${invoiceRef.trim().toUpperCase()}.`,
        });
      },
      onError: (err) => toast({
        variant: "destructive",
        title: "Invoice posting failed",
        description: err.message,
      }),
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <FileCheck className="h-6 w-6 text-primary" />
              Proforma Payments
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Review and confirm suspected payments tied to Merlin quotes or orders.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => refetch()}
              disabled={isLoading || isFetching}
              className="flex items-center gap-2"
            >
              <RefreshCw className={cn("w-4 h-4", (isLoading || isFetching) && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Content Area */}
        {isLoading && !data ? (
          <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error && !data ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 flex items-start gap-3 shadow-sm">
            <AlertCircle className="w-6 h-6 shrink-0" />
            <div>
              <h3 className="font-semibold text-lg">Unable to load proforma payments</h3>
              <p className="text-sm mt-1">{(error as Error).message}</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} />
                Try again
              </Button>
            </div>
          </div>
        ) : data && (
          <div className="space-y-6">
            <div className="flex bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="Search narrative, account, or order..." 
                  className="pl-9 bg-slate-50 border-slate-200" 
                />
              </div>
            </div>

            {payments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
                <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900">No payments found</h3>
                <p className="text-sm text-slate-500 mt-1">No proforma payments match your current criteria.</p>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-4 font-medium">Bank Details</th>
                        <th className="px-5 py-4 font-medium">System Link</th>
                        <th className="px-5 py-4 font-medium">Status & Timeline</th>
                        <th className="px-5 py-4 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payments.map((p) => {
                        // Status mapping
                        const isProcessing = isProformaActivelyProcessing(p.status);
                        const isCompleted = p.status === "posted";
                        const isFailed = p.status === "failed" || p.status === "posting_failed" || p.status === "action_unknown";
                        const isPending = p.status === "pending";
                        
                        // Server decides if we can confirm. Fallback for safety if missing.
                        const canConfirm = p.canConfirm ?? (p.resumable ?? isPending);
                        
                        return (
                          <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                            {/* Bank Details */}
                            <td className="px-5 py-4 align-top">
                              <div className="font-semibold text-slate-900 text-base mb-1">
                                {formatMoney(p.amount)}
                              </div>
                              <div className="text-sm font-mono text-slate-700 bg-slate-100 inline-block px-2 py-0.5 rounded border border-slate-200 mb-2">
                                {p.narrative || "No narrative"}
                              </div>
                              <div className="text-xs text-slate-500">
                                <span className="font-medium">Bank Date:</span> {formatDate(p.paymentDate)}
                              </div>
                            </td>

                            {/* System Link */}
                            <td className="px-5 py-4 align-top">
                              <div className="flex flex-col gap-1.5">
                                {p.account ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-slate-500 w-12">Account</span>
                                    <span className="font-medium text-slate-900">{p.account}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">No account matched</span>
                                )}

                                {p.salesOrder ? (
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className="text-xs text-slate-500 w-12">
                                      {p.documentType === "quote" ? "Quote" : p.documentType === "order" ? "Order" : "Doc"}
                                    </span>
                                    <Badge variant="outline" className="bg-white border-slate-200 text-slate-700 rounded-sm font-mono px-1.5 py-0">
                                      {p.salesOrder}
                                    </Badge>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">No document linked</span>
                                )}
                                
                                {p.returnedSalesOrder && p.returnedSalesOrder !== p.salesOrder && (
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className="text-xs text-slate-500 w-12">Returned</span>
                                    <Badge variant="outline" className="bg-slate-100 border-slate-200 text-slate-600 rounded-sm font-mono px-1.5 py-0">
                                      {p.returnedSalesOrder}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Status & Timeline */}
                            <td className="px-5 py-4 align-top">
                              <div className="mb-2">
                                {p.alreadyPosted ? (
                                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200 shadow-none">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Already posted to Merlin
                                  </Badge>
                                ) : isCompleted ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 shadow-none">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Completed
                                  </Badge>
                                ) : isFailed ? (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border border-red-200 shadow-none">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Failed
                                  </Badge>
                                ) : isProcessing ? (
                                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200 shadow-none">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Processing
                                  </Badge>
                                ) : isPending ? (
                                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border border-blue-200 shadow-none">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    Pending Review
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">{p.status.replace(/_/g, " ")}</Badge>
                                )}
                              </div>

                              <div className="text-[10px] text-slate-500 space-y-0.5">
                                <div><span className="font-medium text-slate-400">Detected:</span> {formatDate(p.detectedAt, true)}</div>
                                {p.confirmedAt && (
                                  <div><span className="font-medium text-slate-400">Confirmed:</span> {formatDate(p.confirmedAt, true)}</div>
                                )}
                                {p.externalActionCompletedAt && (
                                  <div><span className="font-medium text-slate-400">Ext Action:</span> {formatDate(p.externalActionCompletedAt, true)}</div>
                                )}
                                {p.merlinPostedAt && (
                                  <div><span className="font-medium text-slate-400">Posted:</span> {formatDate(p.merlinPostedAt, true)}</div>
                                )}
                              </div>

                              {p.alreadyPosted && (
                                <div className="mt-2 text-xs text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                                  This bank payment has already entered Merlin posting. Do not convert or post it again.
                                </div>
                              )}
                              {isFailed && !p.alreadyPosted && p.failureReason && (
                                <div className="mt-2 text-xs text-red-700 bg-red-50 p-2 rounded border border-red-100">
                                  <strong>Reason:</strong> {p.failureReason}
                                </div>
                              )}

                              {isFailed && (
                                <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                                  <strong>Stage:</strong>{" "}
                                  {p.status === "posting_failed"
                                    ? "Unallocated CashReceipt"
                                    : p.documentType === "quote"
                                      ? "Quote conversion"
                                      : "Sales release email"}
                                  <div className="mt-1">
                                    <strong>Evidence to check:</strong>{" "}
                                    {p.status === "posting_failed"
                                      ? "Merlin submission history and the account's live 999999 balance."
                                      : p.documentType === "quote"
                                        ? "The quote and converted order in SOPHEAD."
                                        : "The outbound email log for this order."}
                                  </div>
                                </div>
                              )}

                              {p.investigation && (
                                <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                                  <div className="font-semibold text-slate-900">
                                    Investigation: {p.investigation.resolution.replace(/_/g, " ")}
                                  </div>
                                  <ul className="mt-1 list-disc space-y-1 pl-4">
                                    {p.investigation.evidence.map((item, index) => {
                                      const text = typeof item === "string" ? item : item.text;
                                      const emailLogId = typeof item === "string" ? undefined : item.emailLogId;
                                      return (
                                        <li key={index}>
                                          {emailLogId ? (
                                            <a
                                              href={`${BASE}/cash-receipts/proforma-payments/${p.id}/evidence/email/${emailLogId}`}
                                              className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:no-underline"
                                            >
                                              {text}
                                              <ExternalLink className="h-3 w-3 shrink-0" />
                                            </a>
                                          ) : text}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  {p.investigation.auditLogId && (
                                    <a
                                      href={`${BASE}/cash-receipts/proforma-payments/${p.id}/evidence/audit/${p.investigation.auditLogId}`}
                                      className="mt-2 inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 hover:no-underline"
                                    >
                                      Open recovery audit entry
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                              )}
                              
                              {p.notes && (
                                <div className="mt-2 text-xs text-slate-600 bg-slate-50 p-2 rounded border border-slate-100">
                                  {p.notes}
                                </div>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4 align-top text-right">
                              <div className="flex flex-col items-end gap-2">
                                {p.canClearAlreadyPosted && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={clearAlreadyPostedMutation.isPending}
                                    onClick={() => clearAlreadyPostedMutation.mutate(p.id, {
                                      onSuccess: () => toast({ title: "False suggestion cleared", description: "The Merlin receipt was preserved." }),
                                      onError: (err) => toast({ variant: "destructive", title: "Could not clear suggestion", description: err.message }),
                                    })}
                                  >
                                    Clear already-posted suggestion
                                  </Button>
                                )}
                                {p.canInvestigate && !p.alreadyPosted && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={investigateMutation.isPending}
                                    onClick={() => investigateMutation.mutate(p.id, {
                                      onSuccess: (result) => toast({
                                        title: "Investigation completed",
                                        description: result.investigation.resolution === "remain_locked"
                                          ? "The payment remains locked. Read the investigation evidence below for the blocker."
                                          : result.investigation.resolution === "resume_cash_receipt"
                                            ? "Click Resume safe stage to finish cash posting only. The quote conversion or Sales email will not be repeated."
                                            : "Evidence proved the safe next stage. The available action has been updated.",
                                      }),
                                      onError: (err) => toast({ variant: "destructive", title: "Investigation failed", description: err.message }),
                                    })}
                                  >
                                    {investigateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                                    Investigate outcome
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => handleConfirmClick(p)}
                                  disabled={!canConfirm}
                                  className={cn(
                                    "font-medium shadow-sm transition-all",
                                    canConfirm ? "bg-primary hover:bg-primary/90 text-white" : "bg-slate-100 text-slate-400 border border-slate-200 opacity-80"
                                  )}
                                >
                                  {p.alreadyPosted
                                    ? "Already posted"
                                    : isProcessing
                                    ? "Processing..."
                                    : isCompleted
                                      ? "Confirmed"
                                      : p.retryableInvalidXml
                                        ? "Retry conversion"
                                        : p.resumable
                                          ? "Resume safe stage"
                                          : "Confirm Payment"}
                                </Button>
                                {p.canPayInvoice && user?.role === "admin" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePayInvoiceClick(p)}
                                    disabled={!p.account || payInvoiceMutation.isPending}
                                  >
                                    Pay invoice
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={(open) => !confirmMutation.isPending && setIsConfirmDialogOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Confirm Proforma Payment</DialogTitle>
            <DialogDescription className="text-slate-600 pt-2 text-sm leading-relaxed">
              {selectedPayment && (
                <>
                  {selectedPayment.resumable ? (
                    <span className="block mb-2">
                      The quote/order action has already completed. This will only post <strong className="text-slate-900 font-semibold">{formatMoney(selectedPayment.amount)} unallocated</strong> to account <strong className="text-slate-900 font-semibold">{selectedPayment.account || "unknown"}</strong>. It will not convert or email the order again.
                    </span>
                  ) : selectedPayment.documentType === 'quote' ? (
                    <span className="block mb-2">
                      This will <strong className="text-slate-900 font-semibold">convert Quote {selectedPayment.salesOrder || "unknown"} to an order</strong> and then post <strong className="text-slate-900 font-semibold">{formatMoney(selectedPayment.amount)} unallocated</strong> to account <strong className="text-slate-900 font-semibold">{selectedPayment.account || "unknown"}</strong>.
                    </span>
                  ) : selectedPayment.documentType === 'order' ? (
                    <span className="block mb-2">
                      This will <strong className="text-slate-900 font-semibold">email Sales to release Order {selectedPayment.salesOrder || "unknown"} for picking</strong> and then post <strong className="text-slate-900 font-semibold">{formatMoney(selectedPayment.amount)} unallocated</strong> to account <strong className="text-slate-900 font-semibold">{selectedPayment.account || "unknown"}</strong>.
                    </span>
                  ) : (
                    <span className="block mb-2">
                      This will process the payment of <strong className="text-slate-900 font-semibold">{formatMoney(selectedPayment.amount)}</strong> for {selectedPayment.salesOrder ? `document ${selectedPayment.salesOrder}` : "an unknown document"}.
                    </span>
                  )}
                  <span className="block p-3 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-xs mt-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Please ensure you have reviewed the bank details carefully before proceeding. This action will be logged.</span>
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={confirmMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={executeConfirm}
              disabled={confirmMutation.isPending}
              className="bg-primary hover:bg-primary/90 text-white min-w-[120px]"
            >
              {confirmMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirming...
                </>
              ) : (
                "Yes, Confirm Payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isInvoiceDialogOpen} onOpenChange={(open) => !payInvoiceMutation.isPending && setIsInvoiceDialogOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Post payment to an invoice</DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed text-slate-600">
              This recovery option does not convert the quote or email Sales. It posts the full payment of{" "}
              <strong className="text-slate-900">{selectedPayment ? formatMoney(selectedPayment.amount) : "—"}</strong>{" "}
              directly to one outstanding invoice on account{" "}
              <strong className="text-slate-900">{selectedPayment?.account || "unknown"}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label htmlFor="proforma-invoice-ref" className="text-sm font-medium text-slate-700">Invoice reference</label>
            <Input
              id="proforma-invoice-ref"
              value={invoiceRef}
              onChange={(event) => setInvoiceRef(event.target.value.toUpperCase())}
              placeholder="For example, I3360123"
              autoFocus
              disabled={payInvoiceMutation.isPending}
            />
            <p className="text-xs text-slate-500">
              The invoice must belong to this account and have enough outstanding balance for the payment.
            </p>
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Confirm the invoice carefully. A successful CashReceipt cannot be undone from this screen.
          </div>
          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setIsInvoiceDialogOpen(false)} disabled={payInvoiceMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={executePayInvoice} disabled={!invoiceRef.trim() || payInvoiceMutation.isPending}>
              {payInvoiceMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Post to invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
